// Runner Market cart — localStorage, SEPARATE from the class cart so product
// orders never mix with class bookings, packages or credits.
// Two line shapes:
//  • tiered sachet product (mitoGEL): keyed by kind:id, holds `sachets` (the
//    chosen sachet count) and `price_tiers`; unit price re-tiers with the count.
//  • flat product / bundle: keyed by kind:id:pack, fixed `price_rm` × `qty`.
const KEY = 'lgra_market_cart_v1';

const read = () => { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; } };
const write = (items) => {
  localStorage.setItem(KEY, JSON.stringify(items));
  window.dispatchEvent(new Event('market-cart-change'));
};

export const itemKey = (i) => `${i.kind}:${i.id}:${i.pack_sachets ?? ''}:${i.variant_sku ?? ''}`;

// Series promo (e.g. LEXI "buy any 2, 5% off") — mirrors backend `_series_discounts`
// so the cart can show the saving before the server quote (or when signed out).
export const seriesDiscounts = (items = read()) => {
  const groups = {};
  for (const i of items) {
    if (!i.series_key || !i.series_promo) continue;
    const g = groups[i.series_key] || (groups[i.series_key] = { series_key: i.series_key, label: i.series_promo.label || 'Series offer', percent: Number(i.series_promo.percent) || 0, min_qty: Number(i.series_promo.min_qty) || 2, qty: 0, eligible_rm: 0 });
    g.qty += lineQty(i); g.eligible_rm += lineTotal(i);
  }
  return Object.values(groups).filter((g) => g.qty >= g.min_qty && g.percent > 0).map((g) => ({ ...g, amount_rm: Math.round(g.eligible_rm * g.percent) / 100 }));
};
export const seriesDiscountTotal = (items = read()) => seriesDiscounts(items).reduce((s, d) => s + d.amount_rm, 0);

// Per-sachet price for a tiered product at a given sachet count.
export const tierUnitPrice = (tiers, n) => {
  const q = Math.max(1, Number(n) || 1);
  for (const t of (tiers || [])) {
    const lo = Number(t.min) || 1;
    const hi = (t.max === null || t.max === undefined) ? null : Number(t.max);
    if (q >= lo && (hi === null || q <= hi)) return Number(t.unit_price_rm) || 0;
  }
  return (tiers && tiers.length) ? Number(tiers[tiers.length - 1].unit_price_rm) || 0 : 0;
};

// Quantity for a line (sachets for tiered, qty otherwise).
export const lineQty = (i) => (i.tiered ? (Number(i.sachets) || 0) : (i.qty || 1));

// Money total for a line.
export const lineTotal = (i) =>
  i.tiered ? tierUnitPrice(i.price_tiers, i.sachets) * (Number(i.sachets) || 0)
           : (Number(i.price_rm) || 0) * (i.qty || 1);

export const getMarketCart = () => read();

// Badge = number of distinct cart lines.
export const marketCartCount = () => read().length;

// addToMarketCart(item): tiered items merge sachet counts; others merge qty.
// Recordings (digital, kind "recording") are capped at ONE per session — adding
// again is a no-op so the cart can never hold a duplicate recording.
export const addToMarketCart = (item) => {
  const items = read();
  const k = itemKey(item);
  const found = items.find((i) => itemKey(i) === k);
  if (found) {
    if (item.kind === 'recording') return items;
    if (item.tiered) {
      const cap = item.max_sachets || found.max_sachets || 99999;
      found.sachets = Math.min(cap, (Number(found.sachets) || 0) + (Number(item.sachets) || 1));
      found.price_tiers = item.price_tiers || found.price_tiers;
      found.max_sachets = cap;
    } else {
      found.qty = Math.min(item.max_qty || found.max_qty || 99999, found.qty + (item.qty || 1));
      if (item.photo) found.photo = item.photo;
    }
  } else {
    items.push(item.tiered ? { sachets: 1, ...item } : { qty: 1, ...item, ...(item.kind === 'recording' ? { qty: 1 } : {}) });
  }
  write(items);
  return items;
};

// Live-session recording → cart line. Returns true when newly added, false if it was already there.
export const addRecordingToCart = (s) => {
  const before = read().length;
  const after = addToMarketCart({
    kind: 'recording', id: s.id, slug: s.slug, name: s.title, coach: s.coach_name, qty: 1,
    price_rm: (s.recording_price_cents || 500) / 100, photo: s.cover_image_url || null,
    preorder: !s.recording_available, sort_order: s.sort_order,
  });
  return after.length > before;
};

export const recordingIdsInCart = () => read().filter((i) => i.kind === 'recording').map((i) => i.id);
export const cartHasPhysical = (items = read()) => items.some((i) => i.kind !== 'recording');

// Set the quantity (qty for flat, sachets for tiered). n<=0 removes the line. Recordings stay at 1.
export const setMarketQty = (key, n) => {
  let items = read();
  const v = Number(n);
  if (v <= 0) items = items.filter((i) => itemKey(i) !== key);
  else items = items.map((i) => (itemKey(i) === key
    ? { ...i, ...(i.kind === 'recording' ? { qty: 1 } : i.tiered ? { sachets: Math.min(i.max_sachets || 99999, v) } : { qty: Math.min(i.max_qty || 99999, v) }) }
    : i));
  write(items);
  return items;
};

export const removeFromMarketCart = (key) => write(read().filter((i) => itemKey(i) !== key));
export const clearMarketCart = () => write([]);
export const marketCartSubtotal = () => read().reduce((s, i) => s + lineTotal(i), 0);
export const marketCartTotal = marketCartSubtotal;
